'use client'

import { memo, useCallback, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { NinxlGrowBar, NinxlScrollReveal } from '@/app/ninxl/_components/ninxl-scroll-reveal'

export type ExpandingFeatureCard = {
  id: string
  title: string
  tagline: string
  description: string
  /** Optional CTA label shown when expanded */
  ctaLabel?: string
  href?: string
  onClick?: () => void
}

export type ExpandingFeatureCardsProps = {
  label?: string
  title: string
  description?: string
  cards: readonly ExpandingFeatureCard[]
  className?: string
  /** Visual slot when a card is expanded (icon / mock / illustration) */
  renderMedia?: (card: ExpandingFeatureCard, active: boolean) => ReactNode
}

const EXPAND_EASE = [0.645, 0.045, 0.355, 1] as const

const CardBody = memo(function CardBody({
  card,
  index,
  expanded,
  onActivate,
  media,
}: {
  card: ExpandingFeatureCard
  index: number
  expanded: boolean
  onActivate: () => void
  media?: ReactNode
}) {
  const indexLabel = String(index + 1).padStart(2, '0')
  const ctaLabel = card.ctaLabel ?? 'Learn more'

  const cta = card.href ? (
    <Link
      href={card.href}
      className="ninxl-pixel-btn inline-flex items-center gap-2 text-sm"
      onClick={(event) => event.stopPropagation()}
    >
      {ctaLabel}
      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
    </Link>
  ) : card.onClick ? (
    <button
      type="button"
      className="ninxl-pixel-btn inline-flex items-center gap-2 text-sm"
      onClick={(event) => {
        event.stopPropagation()
        card.onClick?.()
      }}
    >
      {ctaLabel}
      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
    </button>
  ) : null

  return (
    <div
      role="listitem"
      tabIndex={0}
      onClick={onActivate}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onActivate()
        }
      }}
      aria-expanded={expanded}
      className={cn(
        'marketing-expand-card group relative flex min-h-[12rem] flex-col overflow-hidden border border-[color:var(--ninxl-8bit-border,#21728d)] bg-[color:var(--ninxl-8bit-panel,#1a1a1a)] text-left transition-[flex-grow,flex-basis,filter,min-height] duration-700 md:min-h-[22rem]',
        expanded
          ? 'z-[2] min-h-[18rem] flex-[2.4] border-[color:var(--ninxl-brand-teal)]/45 md:min-h-[22rem]'
          : 'z-[1] flex-1 cursor-pointer opacity-90 hover:opacity-100',
      )}
      style={{ transitionTimingFunction: `cubic-bezier(${EXPAND_EASE.join(',')})` }}
    >
      <div className="relative flex h-full flex-col p-5 md:p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div className="ninxl-8bit-label mb-2 tracking-[0.16em] text-[color:var(--ninxl-brand-teal)]">
              {indexLabel}
            </div>
            <h3 className="ninxl-8bit-heading ninxl-theme-text text-sm md:text-base">
              {card.title}
            </h3>
            <p className="ninxl-8bit-body mt-1 text-sm text-[color:var(--ninxl-8bit-muted)]">
              {card.tagline}
            </p>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {expanded ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0, x: -28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.45, ease: EXPAND_EASE }}
              className="mt-auto flex flex-1 flex-col"
            >
              {media ? <div className="mb-4 flex justify-center py-2">{media}</div> : null}
              <p className="ninxl-8bit-body mb-4 max-w-md text-sm leading-relaxed md:text-base">
                {card.description}
              </p>
              {cta}
            </motion.div>
          ) : (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-auto"
            >
              <span className="ninxl-8bit-label text-[color:var(--ninxl-8bit-muted)]">
                <span className="md:hidden">Tap to expand</span>
                <span className="hidden md:inline">Hover to expand</span>
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
})

/**
 * baby: Webflow /pay expanding cards: one row grows on hover/focus.
 * Content is product-specific; motion language stays shared.
 */
export const ExpandingFeatureCards = memo(function ExpandingFeatureCards({
  label = 'FEATURES',
  title,
  description,
  cards,
  className,
  renderMedia,
}: ExpandingFeatureCardsProps) {
  const reduced = Boolean(useReducedMotion())
  const [activeId, setActiveId] = useState(cards[0]?.id ?? '')

  const onActivate = useCallback((id: string) => {
    setActiveId(id)
  }, [])

  if (cards.length === 0) {
    return null
  }

  return (
    <div className={cn('mx-auto max-w-6xl px-6', className)}>
      <NinxlScrollReveal variant="rise" className="mb-8 text-center md:mb-10">
        <div className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">
          {label}
        </div>
        <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-base md:text-lg">
          {title}
        </h2>
        {description ? (
          <p className="ninxl-8bit-body mx-auto mb-4 max-w-2xl">{description}</p>
        ) : null}
        <div className="mx-auto max-w-[12rem]">
          <NinxlGrowBar />
        </div>
      </NinxlScrollReveal>

      <NinxlScrollReveal delay={0.12} variant="blur">
        <div
          className={cn(
            'flex flex-col gap-3 md:flex-row md:gap-0 md:overflow-hidden md:border md:border-[color:var(--ninxl-8bit-border)]',
            reduced && 'md:flex-wrap',
          )}
          role="list"
        >
          {cards.map((card, index) => {
            const expanded = reduced ? true : activeId === card.id
            return (
              <CardBody
                key={card.id}
                card={card}
                index={index}
                expanded={expanded}
                onActivate={() => onActivate(card.id)}
                media={renderMedia?.(card, expanded)}
              />
            )
          })}
        </div>
      </NinxlScrollReveal>
    </div>
  )
})
